import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentUser } from '../data/auth';
import type { UserInfo } from '../data/types';

interface AuthState {
  user: UserInfo | null;
  roles: string[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useAuth(): AuthState {
  const { t } = useTranslation();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Shared loader for the mount effect and refresh(). State updates happen
  // inside promise callbacks (not synchronously in the function body), which
  // keeps the react-hooks/set-state-in-effect rule satisfied when the mount
  // effect calls this directly.
  const load = useCallback(
    () =>
      getCurrentUser()
        .then((currentUser) => {
          setUser(currentUser);
          setError(null);
        })
        .catch((err: unknown) => {
          setUser(null);
          setError(err instanceof Error ? err.message : t('errors.loadUser'));
        })
        .finally(() => {
          setLoading(false);
        }),
    [t]
  );

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    return load();
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  return { user, roles: user?.userRoles ?? [], loading, error, refresh };
}
